import { createRouter, createWebHistory, type RouteLocationNormalizedLoaded } from 'vue-router';
import Cookie from 'js-cookie';
import { nextTick } from 'vue';

import Login from './components/users/Login.vue';
import Logout from './components/users/Logout.vue';
import Dashboard from './components/dashboard/HomePage.vue';
import DashboardAdmin from './components/dashboard/admin/HomePage.vue';
import PropertyList from './components/dashboard/property/List/Properties.vue';
import DirectoryList from './components/dashboard/directory/list/Directories.vue';
import PropertiesAnalytics from './components/dashboard/property/analytics/PropertyAnalytics.vue';
import PropertiesEnquiries from './components/dashboard/property/enquiries/PropertyEnquiries.vue';
import DirectoriesAnalytics from './components/dashboard/directory/analytics/DirectoriesAnalytics.vue';
import DirectoriesEnquiries from './components/dashboard/directory/enquiries/DirectoryEnquiries.vue';
import Advertise from './components/dashboard/advertise/Advertise.vue';
import AdvertiseDir from './components/dashboard/advertise/AdvertiseDirectory.vue';
import Invoicing from './components/dashboard/invoicing/Invoicing.vue';
import DataFeed from './components/dashboard/dataFeed/DataFeed.vue';
import XmlImportReport from './components/dashboard/xmlimport/Report.vue';
import FAQ from './components/dashboard/faq/Faq.vue';
import TermsOfService from './components/dashboard/termsofservice/TermsOfService.vue';
import Support from './components/dashboard/support/Support.vue';
import NotFound from './components/dashboard/common/NotFound.vue';
import RequestPasswordReset from './components/users/RequestPasswordReset.vue';
import PasswordReset from './components/users/PasswordReset.vue';

export const router = createRouter( {
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            name: 'Login',
            component: Login,
            meta: {
                title: 'Access Control',
            },
        },
        {
            path: '/reset-password',
            name: 'RequestPasswordReset',
            component: RequestPasswordReset,
            meta: {
                title: 'Request Password Reset',
            },
        },
        {
            path: '/set-password',
            name: 'RequestSetPassword',
            component: RequestPasswordReset,
            meta: {
                title: 'Set Password',
                mode: 'set',
            },
        },
        {
            path: '/set-password/:token',
            name: 'SetPassword',
            component: PasswordReset,
            meta: {
                title: 'Set Password',
                mode: 'set',
            },
        },
        {
            path: '/reset-password/:token',
            name: 'ResetPassword',
            component: PasswordReset,
            meta: {
                title: 'Reset Password',
            },
        },
        {
            path: '/logout',
            name: 'Logout',
            component: Logout,
            meta: {
                title: 'Log Out Service',
            },
        },

        /* ADVERTISE */
        {
            path: '/advertise',
            name: 'Advertise',
            component: Advertise,
            meta: {
                title: 'Advertise Property',
            },
        },
        {
            path: '/advertise-directory',
            name: 'AdvertiseDir',
            component: AdvertiseDir,
            meta: {
                title: 'Advertise Directory',
            },
        },
        {
            path: '/invoicing',
            name: 'Invoicing',
            component: Invoicing,
            meta: {
                title: 'Invoicing',
            },
        },
        {
            path: '/termsofservice',
            name: 'TermsOfService',
            component: TermsOfService,
            meta: {
                title: 'Terms of Service',
            },
        },
        {
            path: '/dashboard',
            name: 'Dashboard',
            component: Dashboard,
            meta: {
                title: 'Dashboard',
            },
        },
        {
            path: '/admin',
            name: 'DashboardAdmin',
            component: DashboardAdmin,
            meta: {
                title: 'Admin Dashboard',
            },
        },
        {
            path: '/properties',
            name: 'Properties',
            component: PropertyList,
            meta: {
                title: 'Properties',
            },
        },
        {
            path: '/properties/featured',
            name: 'FeaturedProperties',
            component: PropertyList,
            meta: {
                title: 'Featured Properties',
            },
        },
        {
            path: '/properties/analytics',
            name: 'PropertiesAnalytics',
            component: PropertiesAnalytics,
            meta: {
                title: 'Property Analytics',
            },
        },
        {
            path: '/properties/enquiries',
            name: 'PropertiesEnquiries',
            component: PropertiesEnquiries,
            meta: {
                title: 'Property Enquiries',
            },
        },
        {
            path: '/properties/data-feed',
            name: 'DataFeed',
            component: DataFeed,
            meta: {
                title: 'Data Feed - XML Feeds',
            },
        },
        {
            path: '/properties/xml-import-report',
            name: 'XmlImportReport',
            component: XmlImportReport,
            meta: {
                title: 'XML Import Report',
            },
        },
        {
            path: '/directories',
            name: 'Directories',
            component: DirectoryList,
            meta: {
                title: 'Directories',
            },
        },
        {
            path: '/directories/analytics',
            name: 'DirectoriesAnalytics',
            component: DirectoriesAnalytics,
            meta: {
                title: 'Directory Analytics',
            },
        },
        {
            path: '/directories/enquiries',
            name: 'DirectoriesEnquiries',
            component: DirectoriesEnquiries,
            meta: {
                title: 'Directory Enquiries',
            },
        },
        {
            path: '/support',
            name: 'Support',
            component: Support,
            meta: {
                title: 'Support',
            },
        },
        {
            path: '/faq',
            name: 'FAQ',
            component: FAQ,
            meta: {
                title: 'FAQs',
            },
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            component: NotFound,
            meta: {
                title: '404',
            },
        },
    ],
    scrollBehavior: ( to, from, savedPosition )=> {
        if ( to.path === from.path ) {
            return to;
        }

        if ( savedPosition ) {
            return savedPosition;
        }

        if ( to.hash || from.hash ) {
            return {
                x: 0,
                y: 0,
            };
        }

        return {
            x: 0,
            y: 0,
        };
    },
} );

router.beforeEach( async ( to, from )=> {
    if ( to.query && to.query.lang && to.query.lang.length > 0 && [ 'en', 'es' ].includes( to.query.lang as string ) ) {
        Cookie.set( 'locale', to.query.lang as string, { expires: 14,
            sameSite: 'Strict' } );

        const q = { ...to.query };
        delete q.lang;

        let newRoute = to.path;

        if ( Object.keys( q ).length > 0 ) {
            newRoute += '?' + Object.keys( q ).map( ( k )=> `${k}=` + q[ k ] ).join( '&' );
        }

        window.location.href = newRoute;
        return false;
    }

    return true;
} );

router.afterEach( ( to, from )=> {
    let title = 'thinkSPAIN Client Control Panel';

    if ( to.meta && to.meta.title ) {
        title = `${to.meta.title} | thinkSPAIN Client Control Panel`;
    }

    nextTick( ()=> {
        window.document.title = title;
    } );
} );

export function getRedirectPathFromRoute( r: RouteLocationNormalizedLoaded ): string {
    if ( r.query.redirect && r.query.redirect.length > 0 ) {
        return decodeURIComponent( r.query.redirect as string );
    }
    return '';
}

